/**
 * Custom hook for analytics data using GraphQL with REST fallback
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_FULL_ANALYTICS } from '../graphql/queries';
import ApiService from '../services/api';

export interface AnalyticsData {
  overview: {
    totalOffers: number;
    activeOffers: number;
    totalProducts: number;
    totalMerchants: number;
    avgConfidence: number;
    avgDiscount: number;
    growthRates: {
      offers: number;
      products: number;
      merchants: number;
    };
  };
  timeSeries: Array<{
    date: string;
    offers: number;
    products: number;
    views: number;
    confidence: number;
  }>;
  categories: Array<{
    name: string;
    value: number;
    percentage: number;
    avgDiscount: number;
    color: string;
  }>;
  merchants: Array<{
    name: string;
    offers: number;
    products: number;
    avgDiscount: number;
    performance: number;
  }>;
  confidence: Array<{
    range: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  prices: Array<{
    range: string;
    count: number;
    avgDiscount: number;
  }>;
  geographic: Array<{
    city: string;
    offers: number;
    products: number;
    topMerchant: string;
  }>;
}

export const useAnalyticsData = (dateRange: string, useGraphQL: boolean = true) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // GraphQL query
  const {
    data: graphqlData,
    loading: graphqlLoading,
    error: graphqlError,
    refetch: refetchGraphQL
  } = useQuery(GET_FULL_ANALYTICS, {
    skip: !useGraphQL,
    variables: {
      limit: 100
    },
    fetchPolicy: 'cache-and-network'
  });

  // REST API fallback
  const loadDataWithRest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load data in parallel
      const [offersData, productsData, merchantsData] = await Promise.all([
        ApiService.getAllOffers(),
        ApiService.getProducts({ limit: 1000 }),
        ApiService.getMerchants()
      ]);
      
      // Process and transform data
      const processedData = processRestData(offersData || [], productsData || [], merchantsData || []);
      setData(processedData);
    } catch (err) {
      console.error('Analytics data loading failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Effect for REST fallback
  useEffect(() => {
    if (!useGraphQL || (graphqlError && !graphqlData)) {
      loadDataWithRest();
    }
  }, [useGraphQL, graphqlError, graphqlData, loadDataWithRest]);

  // Transform GraphQL data when available
  useEffect(() => {
    if (graphqlData && useGraphQL) {
      try {
        const transformedData = transformGraphQLData(graphqlData);
        setData(transformedData);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error transforming GraphQL data:', err);
        setError('Failed to process GraphQL data');
        loadDataWithRest();
      }
    }
  }, [graphqlData, useGraphQL, loadDataWithRest]);

  // Set loading state for GraphQL
  useEffect(() => {
    if (useGraphQL) {
      setLoading(graphqlLoading);
      if (graphqlError) {
        setError(graphqlError.message);
      }
    }
  }, [graphqlLoading, graphqlError, useGraphQL]);

  const refetch = async () => {
    if (useGraphQL) {
      return refetchGraphQL();
    } else {
      return loadDataWithRest();
    }
  };

  return {
    data,
    loading,
    error,
    refetch,
    isUsingGraphQL: useGraphQL && !graphqlError,
  };
};

// Transform GraphQL data to expected format
function transformGraphQLData(data: any): AnalyticsData {
  const stats = data.dashboardStats;
  const categoryPerf = data.categoryPerformance || [];
  const merchantPerf = data.merchantPerformance || [];
  
  // Calculate growth rates (mock data for now)
  const growthRates = {
    offers: 12.5,
    products: 23.8,
    merchants: 5.2
  };

  // Process categories
  const totalProducts = stats.products.totalProducts || 1;
  const categories = categoryPerf.map((cat: any, index: number) => ({
    name: cat.name,
    value: cat.productCount,
    percentage: (cat.productCount / totalProducts) * 100,
    avgDiscount: cat.avgDiscount || 0,
    color: getColorByIndex(index)
  }));

  // Process merchants
  const merchants = merchantPerf.map((m: any) => ({
    name: m.merchant.nameArabic || m.merchant.nameEnglish,
    offers: m.offersCount,
    products: m.productsCount,
    avgDiscount: m.avgDiscount || 0,
    performance: (m.avgConfidence || 0) * 100
  }));

  // Generate time series from recent data
  const timeSeries = generateTimeSeriesFromRecent(data.recentOffers || [], data.recentProducts || []);

  // Confidence distribution
  const confidence = [
    {
      range: '90%+',
      count: stats.products.highConfidenceProducts || 0,
      percentage: ((stats.products.highConfidenceProducts || 0) / totalProducts) * 100,
      color: '#22c55e'
    },
    {
      range: '70-89%',
      count: stats.products.mediumConfidenceProducts || 0,
      percentage: ((stats.products.mediumConfidenceProducts || 0) / totalProducts) * 100,
      color: '#eab308'
    },
    {
      range: '50-69%',
      count: Math.max(0, totalProducts - (stats.products.highConfidenceProducts || 0) - (stats.products.mediumConfidenceProducts || 0) - (stats.products.lowConfidenceProducts || 0)),
      percentage: 0,
      color: '#f97316'
    },
    {
      range: '<50%',
      count: stats.products.lowConfidenceProducts || 0,
      percentage: ((stats.products.lowConfidenceProducts || 0) / totalProducts) * 100,
      color: '#ef4444'
    }
  ];

  // Price distribution (generated from product data)
  const prices = generatePriceDistribution(data.recentProducts || []);

  return {
    overview: {
      totalOffers: stats.offers.totalOffers || 0,
      activeOffers: stats.offers.activeOffers || 0,
      totalProducts: stats.products.totalProducts || 0,
      totalMerchants: stats.merchants.totalMerchants || 0,
      avgConfidence: (stats.products.avgConfidence || 0) * 100,
      avgDiscount: calculateAvgDiscount(data.recentProducts || []),
      growthRates
    },
    timeSeries,
    categories,
    merchants,
    confidence,
    prices,
    geographic: [] // Would need geographic data from backend
  };
}

// Process REST API data
function processRestData(offers: any[], products: any[], merchants: any[]): AnalyticsData {
  const totalOffers = offers.length;
  const activeOffers = offers.filter(o => o.status === 'active').length;
  const totalProducts = products.length;
  const totalMerchants = merchants.length;

  // Calculate average confidence
  const avgConfidence = products.length > 0
    ? products.reduce((sum, p) => sum + (p.confidence || 0), 0) / products.length * 100
    : 0;

  // Calculate average discount
  const avgDiscount = calculateAvgDiscount(products);

  // Process categories
  const categoriesMap = new Map();
  products.forEach(product => {
    const category = product.extracted_data?.category || 'غير محدد';
    const current = categoriesMap.get(category) || { count: 0, totalDiscount: 0 };
    categoriesMap.set(category, {
      count: current.count + 1,
      totalDiscount: current.totalDiscount + (product.extracted_data?.discount || 0)
    });
  });

  const categories = Array.from(categoriesMap.entries())
    .map(([name, data], index) => ({
      name,
      value: data.count,
      percentage: (data.count / totalProducts) * 100,
      avgDiscount: data.count > 0 ? data.totalDiscount / data.count : 0,
      color: getColorByIndex(index)
    }))
    .sort((a, b) => b.value - a.value);

  // Process merchants
  const merchantsStats = merchants.map(merchant => {
    const merchantOffers = offers.filter(o => o.merchant_id === merchant.id);
    const merchantProducts = products.filter(p =>
      merchantOffers.some(offer => offer.id === p.offer_id)
    );

    return {
      name: merchant.name_arabic || merchant.name,
      offers: merchantOffers.length,
      products: merchantProducts.length,
      avgDiscount: merchantProducts.length > 0
        ? merchantProducts.reduce((sum, p) => sum + (p.extracted_data?.discount || 0), 0) / merchantProducts.length
        : 0,
      performance: merchantProducts.length > 0
        ? merchantProducts.reduce((sum, p) => sum + (p.confidence || 0), 0) / merchantProducts.length * 100
        : 0
    };
  }).filter(m => m.offers > 0);

  // Generate time series
  const timeSeries = generateTimeSeriesData(offers, products);

  // Confidence distribution
  const confidence = getConfidenceDistribution(products);

  // Price distribution
  const prices = generatePriceDistribution(products);

  return {
    overview: {
      totalOffers,
      activeOffers,
      totalProducts,
      totalMerchants,
      avgConfidence,
      avgDiscount,
      growthRates: {
        offers: 12.5,
        products: 23.8,
        merchants: 5.2
      }
    },
    timeSeries,
    categories,
    merchants: merchantsStats,
    confidence,
    prices,
    geographic: []
  };
}

// Helper functions
function getColorByIndex(index: number): string {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];
  return colors[index % colors.length];
}

function calculateAvgDiscount(products: any[]): number {
  if (products.length === 0) return 0;
  const totalDiscount = products.reduce((sum, p) => {
    const discount = p.extracted_data?.discount || p.extractedData?.discount || 0;
    return sum + discount;
  }, 0);
  return totalDiscount / products.length;
}

function generateTimeSeriesFromRecent(offers: any[], products: any[]): any[] {
  // Group by date
  const dateMap = new Map();
  const now = new Date();
  
  // Generate last 7 days
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    
    dateMap.set(dateStr, {
      date: dateStr,
      offers: 0,
      products: 0,
      views: Math.floor(Math.random() * 1000) + 500,
      confidence: 0
    });
  }

  // Count offers by date
  offers.forEach(offer => {
    const date = new Date(offer.createdAt || offer.created_at).toISOString().split('T')[0];
    if (dateMap.has(date)) {
      const data = dateMap.get(date);
      data.offers++;
    }
  });

  // Count products by date
  products.forEach(product => {
    const date = new Date(product.createdAt || product.created_at).toISOString().split('T')[0];
    if (dateMap.has(date)) {
      const data = dateMap.get(date);
      data.products++;
      data.confidence = (data.confidence * (data.products - 1) + ((product.confidence || product.confidenceScore || 0) * 100)) / data.products;
    }
  });

  return Array.from(dateMap.values());
}

function generateTimeSeriesData(offers: any[], products: any[]): any[] {
  const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو'];
  return months.map((month, index) => ({
    date: month,
    offers: Math.floor(offers.length * (0.1 + index * 0.15)),
    products: Math.floor(products.length * (0.1 + index * 0.15)),
    views: Math.floor(Math.random() * 10000) + 5000,
    confidence: 85 + Math.random() * 10
  }));
}

function getConfidenceDistribution(products: any[]): any[] {
  const ranges = [
    { range: '90%+', count: 0, color: '#22c55e' },
    { range: '70-89%', count: 0, color: '#eab308' },
    { range: '50-69%', count: 0, color: '#f97316' },
    { range: '<50%', count: 0, color: '#ef4444' }
  ];

  products.forEach(product => {
    const confidence = (product.confidence || 0) * 100;
    if (confidence >= 90) ranges[0].count++;
    else if (confidence >= 70) ranges[1].count++;
    else if (confidence >= 50) ranges[2].count++;
    else ranges[3].count++;
  });

  const total = products.length || 1;
  return ranges.map(r => ({
    ...r,
    percentage: (r.count / total) * 100
  }));
}

function generatePriceDistribution(products: any[]): any[] {
  const ranges = [
    { range: '0-50 ريال', count: 0, avgDiscount: 0, totalDiscount: 0 },
    { range: '51-100 ريال', count: 0, avgDiscount: 0, totalDiscount: 0 },
    { range: '101-200 ريال', count: 0, avgDiscount: 0, totalDiscount: 0 },
    { range: '201+ ريال', count: 0, avgDiscount: 0, totalDiscount: 0 }
  ];

  products.forEach(product => {
    const price = product.extracted_data?.price_new || product.extractedData?.priceNew || 0;
    const discount = product.extracted_data?.discount || product.extractedData?.discount || 0;
    
    let rangeIndex = 0;
    if (price <= 50) rangeIndex = 0;
    else if (price <= 100) rangeIndex = 1;
    else if (price <= 200) rangeIndex = 2;
    else rangeIndex = 3;

    ranges[rangeIndex].count++;
    ranges[rangeIndex].totalDiscount += discount;
  });

  return ranges.map(r => ({
    range: r.range,
    count: r.count,
    avgDiscount: r.count > 0 ? r.totalDiscount / r.count : 0
  }));
}